import React, { useEffect, useMemo, useState } from 'react'
import classnames from 'classnames'
import { UtilityType } from '../../../../apis/types'
import CopyableValue from '../../../../components/CopyableValue'
import Badge from '../../../../components/Badge'
import { updateMeterServiceApi } from '../../../../apis/apis'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import { getTenant } from '../Tenants.slice'
import { useParams } from 'react-router-dom'
import OptirateEnrollmentModal from './OptirateEnrollmentModal'
import PortaledTooltip from './PortaledTooltip'
import { STATUS_CHIP_POSSIBLE_VALUES } from './accountStatusUtils'
import StatusChipTooltipContent from './StatusChipTooltipContent'
import ServiceAgreementCloseModal, {
  type ServiceAgreementCloseChoices,
  type ServiceAgreementCloseRequest,
} from './ServiceAgreementCloseModal'
import ServiceAgreementReactivateModal, {
  type ServiceAgreementReactivateChoices,
  type ServiceAgreementReactivateRequest,
} from './ServiceAgreementReactivateModal'
import ServiceAgreementDetail from './ServiceAgreementDetail'
import ServiceAgreementEditModal from './ServiceAgreementEditModal'
import ServiceAgreementCreateModal from './ServiceAgreementCreateModal'
import { Button } from '../../../../components/Form/Button'
import {
  formatPeriodCompact,
  formatServiceAddress,
  LastBilled,
  OptiRateStatus,
  VnemPendingChangeBadge,
  type ServiceAgreementWithAccountNumber,
} from './serviceAgreementDisplay'

export type { ServiceAgreementWithAccountNumber }

type ServiceAgreementView =
  | { view: 'overview' }
  | { view: 'detail'; id: string }

interface TabServiceAgreementsProps {
  title: string
  badge?: number | string
  data: ServiceAgreementWithAccountNumber[]
  hasSalesforceOpsROPermission: () => boolean
  hasSalesforceOpsRWPermission: () => boolean
  hasServiceAgreementSuperuserPermission: () => boolean
  utilityName: string
  tenantAccountId: string
  ccaProgram?: string
}

const TABLE_HEADERS = [
  'ID',
  'Meter',
  'Address',
  'Status',
  'Tariff',
  'CCA',
  'Type',
  'Post-billing',
  'Period',
  'Last billed',
  'VNEM',
  'OptiRate',
] as const

/** Status desc (active first), then period start desc, then period end desc (open end last-as-latest). */
export function compareServiceAgreementsForTable(
  a: ServiceAgreementWithAccountNumber,
  b: ServiceAgreementWithAccountNumber
): number {
  const statusCmp = Number(b.active === true) - Number(a.active === true)
  if (statusCmp !== 0) return statusCmp

  const aStart = a.startDate != null ? new Date(a.startDate).getTime() : 0
  const bStart = b.startDate != null ? new Date(b.startDate).getTime() : 0
  if (bStart !== aStart) return bStart - aStart

  const aEnd = a.endDate != null ? new Date(a.endDate).getTime() : Number.POSITIVE_INFINITY
  const bEnd = b.endDate != null ? new Date(b.endDate).getTime() : Number.POSITIVE_INFINITY
  return bEnd - aEnd
}

const TabServiceAgreements: React.FC<TabServiceAgreementsProps> = (props) => {
  const {
    data,
    hasSalesforceOpsROPermission,
    hasSalesforceOpsRWPermission,
    hasServiceAgreementSuperuserPermission,
    tenantAccountId,
    ccaProgram,
  } = props
  const dispatch = useAppDispatch()
  const { tenantId } = useParams()
  const [currentView, setCurrentView] = useState<ServiceAgreementView>({ view: 'overview' })
  const [isOptirateModalOpen, setIsOptirateModalOpen] = useState(false)
  const [enrollmentMeterServiceCuid, setEnrollmentMeterServiceCuid] = useState<string>('')
  const [isReoptimization, setIsReoptimization] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [closeRequest, setCloseRequest] = useState<ServiceAgreementCloseRequest | null>(null)
  const [isCloseSubmitting, setIsCloseSubmitting] = useState(false)
  const [reactivateRequest, setReactivateRequest] = useState<ServiceAgreementReactivateRequest | null>(
    null
  )
  const [isReactivateSubmitting, setIsReactivateSubmitting] = useState(false)
  const [editRecord, setEditRecord] = useState<ServiceAgreementWithAccountNumber | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const canRw = hasSalesforceOpsRWPermission()
  const canRo = hasSalesforceOpsROPermission()
  const canSuperuser = hasServiceAgreementSuperuserPermission()

  const refreshTenant = () => dispatch(getTenant(tenantId as string)).unwrap()

  // If the selected SA disappears after refresh, fall back to overview.
  useEffect(() => {
    if (currentView.view !== 'detail') return
    if (!data.some((sa) => sa.id === currentView.id)) {
      setCurrentView({ view: 'overview' })
    }
  }, [data, currentView])

  const handleCloseConfirm = async (choices: ServiceAgreementCloseChoices): Promise<void> => {
    if (closeRequest == null) return
    setIsCloseSubmitting(true)
    try {
      const payload: { active?: boolean; endDate?: string | null } = {}
      if (choices.deactivate) payload.active = false
      if (choices.setEndDate) payload.endDate = choices.endDate
      await updateMeterServiceApi(closeRequest.meterServiceId, payload)
      await refreshTenant()
      setCloseRequest(null)
    } catch (error) {
      console.error('Service agreement close update failed:', error)
    } finally {
      setIsCloseSubmitting(false)
    }
  }

  const handleReactivateConfirm = async (choices: ServiceAgreementReactivateChoices): Promise<void> => {
    if (reactivateRequest == null) return
    setIsReactivateSubmitting(true)
    try {
      const payload: { active: boolean; startDate: string; endDate?: string | null } = {
        active: true,
        startDate: choices.startDate,
      }
      if (choices.clearEndDate) payload.endDate = null
      await updateMeterServiceApi(reactivateRequest.meterServiceId, payload)
      await refreshTenant()
      setReactivateRequest(null)
    } catch (error) {
      console.error('Service agreement reactivate update failed:', error)
      void dispatch(
        pushToast({
          type: 'error',
          message: 'Could not reactivate service agreement',
          description: error instanceof Error ? error.message : undefined,
        })
      )
    } finally {
      setIsReactivateSubmitting(false)
    }
  }

  const { inactiveAgreements, visibleAgreements } = useMemo(() => {
    const active = data.filter((sa) => sa.active === true)
    const inactive = data.filter((sa) => sa.active !== true)
    const rows = showInactive ? [...data] : [...active]
    rows.sort(compareServiceAgreementsForTable)
    return {
      inactiveAgreements: inactive,
      visibleAgreements: rows,
    }
  }, [data, showInactive])

  const selectedRecord =
    currentView.view === 'detail' ? data.find((sa) => sa.id === currentView.id) : undefined

  const modals = (
    <>
      <OptirateEnrollmentModal
        isOpen={isOptirateModalOpen}
        onClose={() => {
          setIsOptirateModalOpen(false)
          setEnrollmentMeterServiceCuid('')
          setIsReoptimization(false)
        }}
        onSuccess={() => void refreshTenant()}
        selectedMeterServiceCuid={enrollmentMeterServiceCuid}
        utilityShortName={props.utilityName as UtilityType}
        isReoptimization={isReoptimization}
      />
      <ServiceAgreementCloseModal
        isOpen={closeRequest != null}
        request={closeRequest}
        isSubmitting={isCloseSubmitting}
        onClose={() => {
          if (!isCloseSubmitting) setCloseRequest(null)
        }}
        onConfirm={handleCloseConfirm}
      />
      <ServiceAgreementReactivateModal
        isOpen={reactivateRequest != null}
        request={reactivateRequest}
        isSubmitting={isReactivateSubmitting}
        onClose={() => {
          if (!isReactivateSubmitting) setReactivateRequest(null)
        }}
        onConfirm={handleReactivateConfirm}
      />
      <ServiceAgreementEditModal
        isOpen={editRecord != null}
        record={editRecord}
        ccaProgram={ccaProgram}
        onClose={() => setEditRecord(null)}
        onSaved={async () => {
          await refreshTenant()
        }}
      />
      <ServiceAgreementCreateModal
        isOpen={isCreateOpen}
        tenantAccountId={tenantAccountId}
        existingAgreements={data}
        ccaProgram={ccaProgram}
        onClose={() => setIsCreateOpen(false)}
        onSaved={async () => {
          await refreshTenant()
        }}
      />
    </>
  )

  if (currentView.view === 'detail' && selectedRecord != null) {
    return (
      <>
        <ServiceAgreementDetail
          record={selectedRecord}
          allRows={data}
          utilityName={props.utilityName}
          canRw={canRw}
          canRo={canRo}
          canSuperuser={canSuperuser}
          ccaProgram={ccaProgram}
          isCloseSubmitting={isCloseSubmitting || isReactivateSubmitting}
          onBack={() => setCurrentView({ view: 'overview' })}
          onRequestClose={setCloseRequest}
          onRequestReactivate={setReactivateRequest}
          onRefresh={refreshTenant}
          onEnrollOptiRate={(meterServiceCuid, isReoptimization) => {
            setEnrollmentMeterServiceCuid(meterServiceCuid)
            setIsReoptimization(isReoptimization)
            setIsOptirateModalOpen(true)
          }}
          onEdit={() => setEditRecord(selectedRecord)}
        />
        {modals}
      </>
    )
  }

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 p-0.5">
        <div />
        {canSuperuser ? (
          <Button type="button" size={2} onClick={() => setIsCreateOpen(true)}>
            Add service agreement
          </Button>
        ) : null}
      </div>
      {data.length === 0 ? (
        <span>No service agreements found</span>
      ) : (
        <div className="space-y-2">
          {visibleAgreements.length === 0 && (
            <p className="text-sm text-slate-500 dark:text-slate-400">No active service agreements</p>
          )}

          <div className={visibleAgreements.length === 0 ? 'hidden' : 'overflow-x-auto'}>
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="bg-slate-50 dark:bg-slate-900">
                <tr>
                  {TABLE_HEADERS.map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2 text-left text-xs font-medium normal-case text-slate-500 dark:text-slate-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {visibleAgreements.map((record) => {
                  const isInactive = record.active !== true
                  const addressLine = formatServiceAddress(record)

                  return (
                    <tr
                      key={record.id}
                      className={classnames(
                        'cursor-pointer transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-950/30',
                        isInactive
                          ? 'bg-slate-50/40 opacity-80 dark:bg-slate-950/30'
                          : 'bg-slate-50 dark:bg-slate-950/50'
                      )}
                      onClick={() => setCurrentView({ view: 'detail', id: record.id })}
                    >
                      <td className="whitespace-nowrap px-3 py-2 text-sm">
                        {record.serviceAgreementId ? (
                          <CopyableValue
                            value={record.serviceAgreementId}
                            label="service agreement ID"
                            truncate={false}
                            valueClassName="font-mono text-sm tracking-tight text-slate-900 dark:text-slate-100"
                          />
                        ) : (
                          <span className="font-mono text-slate-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm">
                        {record.meter.meterNumber ? (
                          <CopyableValue
                            value={record.meter.meterNumber}
                            label="meter number"
                            truncate={false}
                            valueClassName="font-mono text-sm tracking-tight text-slate-700 dark:text-slate-200"
                          />
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="max-w-[12rem] px-3 py-2 text-sm text-slate-600 dark:text-slate-300">
                        <span className="block truncate" title={addressLine || undefined}>
                          {addressLine || '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <Badge status={record.active ? 'ACTIVE' : 'INACTIVE'} variant="soft" />
                      </td>
                      <td className="px-3 py-2">
                        {record.tariff?.schedule ? (
                          <Badge
                            status="UTILITY"
                            displayLabel={record.tariff.schedule}
                            variant="soft"
                          />
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {record.ccaTariff?.schedule ? (
                          <PortaledTooltip
                            content={
                              <div className="space-y-0.5 text-xs">
                                <div className="font-medium text-slate-100">CCA tariff</div>
                                <div>
                                  {record.ccaTariff.utilityShortName
                                    ? `${record.ccaTariff.utilityShortName} · ${record.ccaTariff.schedule}`
                                    : record.ccaTariff.schedule}
                                </div>
                              </div>
                            }
                            className="inline-flex w-fit shrink-0"
                          >
                            <Badge status="ENROLLED" displayLabel="CCA" variant="soft" />
                          </PortaledTooltip>
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {record.meterAccountType === 'GENERATOR' ||
                        record.meterAccountType === 'BENEFITTING' ? (
                          <Badge
                            status={record.meterAccountType}
                            displayLabel={
                              record.meterAccountType === 'GENERATOR' ? 'Generator' : 'Benefitting'
                            }
                            variant="soft"
                          />
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {record.postBillingTransition === true ? (
                          <Badge status="YES" variant="soft" />
                        ) : record.postBillingTransition === false ? (
                          <Badge status="NO" variant="soft" />
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm dark:text-slate-300">
                        {formatPeriodCompact(record)}
                      </td>
                      <td className="px-3 py-2">
                        <LastBilled record={record} />
                      </td>
                      <td className="px-3 py-2">
                        {record.vnemStatus != null ? (
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                            <PortaledTooltip
                              content={
                                <StatusChipTooltipContent
                                  label="VNEM Status"
                                  values={[...STATUS_CHIP_POSSIBLE_VALUES.vnemStatus]}
                                  currentStatus={record.vnemStatus}
                                />
                              }
                              className="inline-flex w-fit shrink-0"
                            >
                              <Badge status={record.vnemStatus} variant="soft" />
                            </PortaledTooltip>
                            {record.vnemPendingChange != null ? (
                              <VnemPendingChangeBadge change={record.vnemPendingChange} />
                            ) : null}
                          </div>
                        ) : (
                          <span className="text-sm text-slate-500 dark:text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <OptiRateStatus record={record} allRows={data} compact />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {inactiveAgreements.length > 0 && (
            <div className="pt-1">
              <button
                type="button"
                onClick={() => setShowInactive((v) => !v)}
                className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              >
                {showInactive
                  ? `Hide ${inactiveAgreements.length} inactive agreement${inactiveAgreements.length > 1 ? 's' : ''}`
                  : `Show ${inactiveAgreements.length} inactive agreement${inactiveAgreements.length > 1 ? 's' : ''}`}
              </button>
            </div>
          )}
        </div>
      )}

      {modals}
    </>
  )
}

export default TabServiceAgreements
